import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  type CollectionReference,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { wordleScoreDocumentConverter } from "@kekkle/shared/convertors/web/wordle-score-document.converter";
import type { WorldeScoreDocument } from "@kekkle/shared/types/documents/wordle-score-document/wordle-score-document";
import type {
  FetchGameScoresOptions,
  FetchGameScoresResult,
} from "@/types/firestore/wordle";

/**
 * Returns a reference to the wordle scores collection with converter
 */
function wordleScoresCollection(): CollectionReference<WorldeScoreDocument> {
  return collection(firestore, "wordle_scores").withConverter(
    wordleScoreDocumentConverter,
  );
}

/**
 * Fetches the scores of a game, based on the game ID and user filter
 * @param gameId - Game ID
 * @param userFilter - Array of user IDs to filter by (optional)
 * @param options - Pagination options
 * @returns Object containing documents, last document snapshot, and hasMore flag
 */
export async function fetchGameScores(
  gameId: string,
  userFilter: string[] = [],
  options: FetchGameScoresOptions = {},
): Promise<FetchGameScoresResult> {
  const { pageSize = 10, lastDoc } = options;
  let docs: WorldeScoreDocument[] = [];
  let finalLastDoc: QueryDocumentSnapshot<DocumentData> | undefined;
  // Firestore only supports up to 30 in queries in a single query
  const userFilterBatch: string[][] = [];

  // Split the userFilter into multiple arrays in the userFilterBatch array
  for (let i = 0; i < userFilter.length; i += 25) {
    userFilterBatch.push(userFilter.slice(i, i + 25));
  }

  // Loop through the userFilterBatch array
  for (let i = 0; i < userFilterBatch.length || i === 0; i++) {
    let scoresQuery = query(
      wordleScoresCollection(),
      where("game_id", "==", gameId),
      orderBy("new_score", "desc"),
    );

    if (userFilterBatch[i]) {
      scoresQuery = query(
        scoresQuery,
        where("user_id", "in", userFilterBatch[i]),
      );
    }

    if (lastDoc && i === 0) {
      scoresQuery = query(scoresQuery, startAfter(lastDoc));
    }

    scoresQuery = query(scoresQuery, limit(pageSize));

    const querySnapshot = await getDocs(scoresQuery);
    const documents = querySnapshot.docs.map((doc) => doc.data());
    docs = docs.concat(documents);

    if (querySnapshot.docs.length > 0) {
      finalLastDoc = querySnapshot.docs[
        querySnapshot.docs.length - 1
      ] as QueryDocumentSnapshot<DocumentData>;
    }

    // Only fetch from first batch for pagination
    if (docs.length >= pageSize) break;
  }

  return {
    docs,
    lastDoc: finalLastDoc,
    hasMore: docs.length === pageSize,
  };
}
